/**
 * Informational modal with header, optional badges, and stacked sections.
 */

import * as React from 'react';
import { BaseModal } from './BaseModal';
import { type LucideIcon } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { cn } from '@/lib/utils';

/** One stacked content block in {@link InfoModal}. */
interface InfoSection {
  id: string;
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  title?: string;
  content: React.ReactNode;
  className?: string;
}

/** Pill shown in the badge row under the {@link InfoModal} header. */
interface InfoBadge {
  id: string;
  icon?: LucideIcon;
  label: string;
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
}

/** Footer button for {@link InfoModal} (preset or custom variant). */
interface InfoAction {
  id: string;
  label: string;
  onClick: () => void;
  preset?: 'modal-cancel' | 'modal-edit' | 'modal-confirm' | 'modal-danger';
  variant?: 'primary' | 'secondary' | 'cancel' | 'danger' | 'ghost' | 'custom';
  icon?: LucideIcon;
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

/** Props for {@link InfoModal}. */
interface InfoModalProps {
  open: boolean;
  onClose: () => void;
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  badge?: React.ReactNode;
  title: string;
  titleColor?: string;
  titleSize?: 'sm' | 'md' | 'lg' | 'xl';
  subtitle?: string;
  badges?: InfoBadge[];
  sections?: InfoSection[];
  children?: React.ReactNode;
  actions?: InfoAction[];
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  className?: string;
  /** Calendar event type, applied as data-event-type on the dialog. */
  dataEventType?: string;
  /** Keep the footer slot even when `actions` is empty. */
  reserveFooter?: boolean;
  /** When false, the orange card hugs its children instead of filling the body. */
  fillContent?: boolean;
}

/** One footer button: custom style when asked, otherwise the preset. */
function renderInfoAction(action: InfoAction): React.ReactNode {
  if (action.variant === 'custom' && (action.className || action.style)) {
    return (
      <Button
        key={action.id}
        variant="custom"
        size="sm"
        onClick={action.onClick}
        disabled={action.disabled}
        className={cn(
          'px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200',
          'hover:opacity-90 active:scale-95',
          action.disabled && 'opacity-50 cursor-not-allowed',
          action.className,
        )}
        style={action.className ? undefined : action.style}
      >
        {action.label}
      </Button>
    );
  }
  return (
    <Button
      key={action.id}
      preset={action.preset}
      variant={action.preset ? undefined : action.variant || 'primary'}
      onClick={action.onClick}
      icon={action.icon}
      loading={action.loading}
      disabled={action.disabled}
    >
      {action.label}
    </Button>
  );
}

/** Footer actions row, or an empty reserved slot when asked. */
function resolveInfoFooter(
  actions: InfoAction[] | undefined,
  reserveFooter: boolean,
): React.ReactNode {
  if (actions && actions.length > 0) {
    return <div className="flex gap-2 justify-end">{actions.map(renderInfoAction)}</div>;
  }
  if (reserveFooter) {
    return <div aria-hidden className="h-8" />;
  }
  return undefined;
}

/** Informational dialog built on {@link BaseModal}. */
export const InfoModal: React.FC<InfoModalProps> = ({
  open,
  onClose,
  icon,
  iconColor,
  iconBgColor,
  iconSize = 'md',
  badge,
  title,
  titleColor,
  titleSize = 'xl',
  subtitle,
  badges,
  sections,
  children,
  actions,
  size = 'lg',
  className,
  dataEventType,
  reserveFooter = false,
  fillContent = true,
}) => {
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size={size}
      className={className}
      dataEventType={dataEventType}
      fillContent={fillContent}
      header={{
        icon,
        iconColor,
        iconBgColor,
        iconSize,
        badge,
        title,
        titleColor,
        titleSize,
        titleWeight: 'bold',
        subtitle,
      }}
      footer={resolveInfoFooter(actions, reserveFooter)}
    >
      {badges && badges.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5">
          {badges.map((badgeItem) => {
            const BadgeIcon = badgeItem.icon;
            return (
              <div
                key={badgeItem.id}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
                  badgeItem.borderColor && 'border',
                )}
                style={{
                  backgroundColor:
                    badgeItem.bgColor ||
                    'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
                  color: badgeItem.textColor || 'var(--color-2-nuance-1)',
                  borderColor: badgeItem.borderColor,
                }}
              >
                {BadgeIcon && (
                  <BadgeIcon
                    className="w-4 h-4"
                    style={{ color: badgeItem.textColor || 'var(--color-2-nuance-1)' }}
                  />
                )}
                <span>{badgeItem.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {sections && sections.length > 0 && (
        <div className="space-y-5">
          {sections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div
                key={section.id}
                className={cn(
                  'flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-[var(--color-1-nuance-3)]/30',
                  section.className,
                )}
              >
                {SectionIcon && (
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0"
                    style={{
                      backgroundColor:
                        section.iconBgColor ||
                        'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
                    }}
                  >
                    <SectionIcon
                      className="w-5 h-5"
                      style={{ color: section.iconColor || 'var(--color-2-nuance-1)' }}
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {section.title && (
                    <p className="font-semibold text-foreground mb-1">{section.title}</p>
                  )}
                  <div className="text-sm text-muted-foreground">{section.content}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {children && !sections && <div>{children}</div>}
    </BaseModal>
  );
};
